import CloseIcon from '@mui/icons-material/Close';
import { Box, Divider, IconButton } from '@mui/material';
import type { SerializedError } from '@reduxjs/toolkit';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  TextField,
  Typography,
} from '@shared/ui';
import { type ReactElement, useEffect, useState } from 'react';
import { useAddManualTranscriptMutation } from '../api/sessionApi';

// Copy for each error code the manual-transcript API returns (MEN-5260).
const ERROR_MESSAGES: Record<string, string> = {
  transcript_unusable:
    'This doesn’t look like a transcript. Paste the conversation with each line starting with the speaker’s name, for example “Coach: …”.',
  transcript_too_large: 'This transcript is too long. Paste up to 500 KB of text.',
  transcript_exists: 'This session already has a transcript.',
  session_not_eligible: 'A transcript can’t be added to this session.',
  forbidden: 'Only the session’s coach can add its transcript.',
};
const DEFAULT_ERROR_MESSAGE = 'The transcript couldn’t be saved. Try again.';

export function manualTranscriptErrorMessage(
  error: FetchBaseQueryError | SerializedError | undefined
): string {
  if (error && 'status' in error && error.data && typeof error.data === 'object') {
    const code = (error.data as { error?: unknown }).error;
    if (typeof code === 'string' && ERROR_MESSAGES[code]) {
      return ERROR_MESSAGES[code];
    }
  }
  return DEFAULT_ERROR_MESSAGE;
}

export interface PasteTranscriptDialogProps {
  open: boolean;
  sessionId: string;
  sessionDateLabel: string;
  memberLabel: string;
  onClose: () => void;
  onSaved: (sessionId: string) => void;
}

const BUTTON_SX = {
  borderRadius: '12px',
  fontSize: '13px',
  fontWeight: 700,
  letterSpacing: '0.4px',
  lineHeight: '24px',
  padding: '6px 16px',
  textTransform: 'none',
} as const;

export default function PasteTranscriptDialog({
  open,
  sessionId,
  sessionDateLabel,
  memberLabel,
  onClose,
  onSaved,
}: PasteTranscriptDialogProps): ReactElement {
  const [transcript, setTranscript] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [addManualTranscript, { isLoading }] = useAddManualTranscriptMutation();

  useEffect(() => {
    if (open) {
      setTranscript('');
      setErrorMessage(null);
    }
  }, [open]);

  const handleSave = async () => {
    setErrorMessage(null);
    const result = await addManualTranscript({ sessionId, transcript });
    if ('error' in result) {
      setErrorMessage(manualTranscriptErrorMessage(result.error));
      return;
    }
    onSaved(sessionId);
  };

  return (
    <Dialog
      aria-labelledby="paste-transcript-title"
      onClose={isLoading ? undefined : onClose}
      open={open}
      PaperProps={{ sx: { borderRadius: '16px', maxWidth: 486, width: '100%' } }}
    >
      <Box sx={{ alignItems: 'flex-start', display: 'flex', gap: 2, p: '32px 32px 16px' }}>
        <Box sx={{ flex: 1 }}>
          <Typography
            component="h2"
            id="paste-transcript-title"
            sx={{ color: '#2B2E2B', fontSize: 15, fontWeight: 700 }}
          >
            Paste a Transcript
          </Typography>
          <Typography sx={{ color: '#9AA49D', fontSize: 11 }}>
            For the {sessionDateLabel} session with {memberLabel}
          </Typography>
        </Box>
        <IconButton aria-label="Close" disabled={isLoading} onClick={onClose} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
      <DialogContent sx={{ p: '0 32px 32px' }}>
        <TextField
          autoFocus
          disabled={isLoading}
          error={Boolean(errorMessage)}
          fullWidth
          helperText={errorMessage ?? undefined}
          inputProps={{ 'aria-label': 'Transcript' }}
          minRows={14}
          maxRows={14}
          multiline
          onChange={(event) => setTranscript(event.target.value)}
          sx={{ '& textarea': { resize: 'none' } }}
          value={transcript}
        />
      </DialogContent>
      <Divider />
      <DialogActions sx={{ gap: 1, p: '16px' }}>
        <Button color="gray" disabled={isLoading} onClick={onClose} sx={BUTTON_SX} variant="outlined">
          Cancel
        </Button>
        <Button
          color="primary"
          disabled={isLoading || transcript.trim() === ''}
          onClick={() => void handleSave()}
          sx={BUTTON_SX}
          variant="contained"
        >
          {isLoading ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
